import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * KioskForce ERP — trilingual i18n engine (EN · EL · AR).
 *
 * Design rules
 * ────────────
 * • Language token persists in localStorage under `kioskforce.lang`.
 * • Switching the token also flips `<html dir>` and `<html lang>` so
 *   Arabic naturally inverts to RTL across the entire layout, including
 *   tables, dialogs, forms and the menu rail.
 * • Strings live in a single flat dictionary keyed by dotted paths
 *   (e.g. `endShift.title`). Calling `t("endShift.title")` returns the
 *   string in the active language. Missing keys fall back to English,
 *   then to the raw key itself.
 * • Use `useLang()` if you only need the active code; `useT()` for the
 *   resolver function. Both are cheap (no re-render on unrelated state).
 */

const STORAGE_KEY = "kioskforce.lang";
const DEFAULT_LANG = "en";

export const SUPPORTED_LANGS = [
  { code: "en", label: "English",          flag: "🇬🇧", nativeLabel: "English" },
  { code: "el", label: "Ελληνικά",         flag: "🇨🇾", nativeLabel: "Ελληνικά" },
  { code: "ar", label: "العربية (Arabic)", flag: "🇸🇦", nativeLabel: "العربية" },
];

export const isRTL = (code) => code === "ar";

// ─────────────────────────────────────────────────────────────────────
// Dictionary — flat keys, dotted paths. Add new strings as the product
// grows; missing keys gracefully degrade to English.
// ─────────────────────────────────────────────────────────────────────
const DICT = {
  // ── App-level / brand
  "brand.tagline.master":       { en: "Cyprus · 4 stores",         el: "Κύπρος · 4 περίπτερα",       ar: "قبرص · 4 أكشاك" },
  "brand.tagline.tenant":       { en: "Multi-store tenant",         el: "Πελάτης πολλαπλών καταστημάτων", ar: "متعدد المتاجر" },

  // ── AppLock
  "applock.title":              { en: "KioskForce ERP Access",      el: "Πρόσβαση KioskForce ERP",   ar: "الوصول إلى KioskForce" },
  "applock.subtitle":           { en: "Restricted area · Sign in to continue", el: "Περιορισμένη περιοχή · Συνδεθείτε για να συνεχίσετε", ar: "منطقة محظورة · سجل الدخول للمتابعة" },
  "applock.username":           { en: "Username",                   el: "Όνομα Χρήστη",              ar: "اسم المستخدم" },
  "applock.password":           { en: "Password",                   el: "Κωδικός",                    ar: "كلمة المرور" },
  "applock.signIn":             { en: "Sign In",                    el: "Σύνδεση",                    ar: "تسجيل الدخول" },
  "applock.error":              { en: "Invalid credentials, please try again", el: "Λανθασμένα διαπιστευτήρια, δοκιμάστε ξανά", ar: "بيانات اعتماد غير صحيحة، حاول مرة أخرى" },
  "applock.footer":             { en: "Authorised personnel only · KioskForce ERP", el: "Μόνο εξουσιοδοτημένο προσωπικό · KioskForce ERP", ar: "للأفراد المصرح لهم فقط · KioskForce ERP" },

  // ── Nav tabs
  "nav.login":                  { en: "NEW LOGIN",                  el: "ΝΕΑ ΣΥΝΔΕΣΗ",                ar: "تسجيل دخول جديد" },
  "nav.invoices":               { en: "INVOICES",                   el: "ΤΙΜΟΛΟΓΙΑ",                  ar: "الفواتير" },
  "nav.cashflow":               { en: "CASHFLOW",                   el: "ΤΑΜΕΙΑΚΗ ΡΟΗ",               ar: "الحركة النقدية" },
  "nav.endShift":               { en: "END SHIFT",                  el: "ΤΕΛΟΣ ΒΑΡΔΙΑΣ",              ar: "إنهاء الوردية" },
  "nav.liveView":               { en: "LIVE VIEW",                  el: "ΖΩΝΤΑΝΗ ΠΡΟΒΟΛΗ",            ar: "العرض المباشر" },
  "nav.kronos":                 { en: "KRONOS",                     el: "ΚΡΟΝΟΣ",                     ar: "كرونوس" },
  "nav.chat":                   { en: "💬 CHAT",                    el: "💬 ΣΥΝΟΜΙΛΙΑ",              ar: "💬 محادثة" },
  "nav.payroll":                { en: "🕒 PAYROLL",                 el: "🕒 ΜΙΣΘΟΔΟΣΙΑ",             ar: "🕒 الرواتب" },
  "nav.cheques":                { en: "💳 CHEQUES",                 el: "💳 ΕΠΙΤΑΓΕΣ",                ar: "💳 الشيكات" },
  "nav.portal":                 { en: "PORTAL",                     el: "ΠΥΛΗ",                       ar: "البوابة" },
  "nav.settings":               { en: "SETTINGS",                   el: "ΡΥΘΜΙΣΕΙΣ",                  ar: "الإعدادات" },
  "nav.active":                 { en: "Active",                     el: "Ενεργή",                     ar: "نشط" },
  "nav.langPicker.label":       { en: "Language",                   el: "Γλώσσα",                     ar: "اللغة" },

  // ── Common buttons & atomic terms
  "common.addToShift":          { en: "+ ADD TO SHIFT",             el: "+ ΠΡΟΣΘΗΚΗ ΣΤΗ ΒΑΡΔΙΑ",      ar: "+ إضافة إلى الوردية" },
  "common.save":                { en: "Save",                       el: "Αποθήκευση",                 ar: "حفظ" },
  "common.cancel":              { en: "Cancel",                     el: "Άκυρο",                      ar: "إلغاء" },
  "common.discard":             { en: "Discard",                    el: "Απόρριψη",                   ar: "تجاهل" },
  "common.delete":              { en: "Delete",                     el: "Διαγραφή",                   ar: "حذف" },
  "common.startShift":          { en: "START SHIFT",                el: "ΕΝΑΡΞΗ ΒΑΡΔΙΑΣ",             ar: "بدء الوردية" },
  "common.endShift":            { en: "End Shift",                  el: "Τερματισμός Βάρδιας",       ar: "إنهاء الوردية" },
  "common.float":               { en: "Float",                      el: "Αρχικό Ταμείο",              ar: "الرصيد الأولي" },
  "common.expectedCash":        { en: "Expected Cash",              el: "Αναμενόμενο Ταμείο",         ar: "النقد المتوقع" },
  "common.countedCash":         { en: "Counted Cash",               el: "Μετρημένο Ταμείο",           ar: "النقد المعدود" },
  "common.discrepancy":         { en: "Discrepancy",                el: "Απόκλιση",                   ar: "الفرق" },
  "common.invoices":            { en: "Invoices",                   el: "Τιμολόγια",                  ar: "الفواتير" },
  "common.cashflow":            { en: "Cashflow",                   el: "Ταμειακή Ροή",               ar: "الحركة النقدية" },
  "common.supplierDebtMatrix":  { en: "Supplier Debt Matrix",       el: "Μήτρα Οφειλών Προμηθευτών", ar: "مصفوفة ديون الموردين" },
  "common.taxForm4":            { en: "TFA Form 4 · VAT Return",    el: "TFA Έντυπο 4 · Δήλωση ΦΠΑ",  ar: "نموذج TFA 4 · إقرار ضريبة القيمة المضافة" },

  // ── New Login (Start Shift)
  "newLogin.title":             { en: "Start Shift",                el: "Έναρξη Βάρδιας",            ar: "بدء الوردية" },
  "newLogin.subtitle":          { en: "Pick a kiosk, register and your name to open the drawer.", el: "Επιλέξτε κατάστημα, ταμείο και όνομα για να ανοίξετε το συρτάρι.", ar: "اختر الكشك والصندوق واسمك لفتح الدرج." },
  "newLogin.location":          { en: "Location",                   el: "Κατάστημα",                  ar: "الموقع" },
  "newLogin.register":          { en: "Register",                   el: "Ταμείο",                     ar: "الصندوق" },
  "newLogin.employee":          { en: "Employee Name",              el: "Όνομα Υπαλλήλου",            ar: "اسم الموظف" },
  "newLogin.starting":          { en: "Starting Float (€)",         el: "Αρχικό Ταμείο (€)",         ar: "الرصيد الأولي (€)" },

  // ── Invoices
  "invoices.title":             { en: "Invoices",                   el: "Τιμολόγια",                  ar: "الفواتير" },
  "invoices.subtitle":          { en: "Capture supplier invoices and route them to the shift.", el: "Καταγράψτε τιμολόγια προμηθευτών και προωθήστε τα στη βάρδια.", ar: "التقط فواتير الموردين ووجّهها إلى الوردية." },

  // ── Cashflow
  "cashflow.title":             { en: "Cashflow",                   el: "Ταμειακή Ροή",               ar: "الحركة النقدية" },
  "cashflow.subtitle":          { en: "Payouts, cash drops and staff payments.", el: "Πληρωμές, αποθέσεις μετρητών και πληρωμές προσωπικού.", ar: "المدفوعات، الإيداعات النقدية، ومدفوعات الموظفين." },

  // ── End Shift
  "endShift.title":             { en: "End Shift",                  el: "Τερματισμός Βάρδιας",       ar: "إنهاء الوردية" },
  "endShift.subtitle":          { en: "Close Shift",                el: "Τερματισμός Βάρδιας",       ar: "إغلاق الوردية" },
  "endShift.expected":          { en: "Expected",                   el: "Αναμενόμενο",                ar: "المتوقع" },
  "endShift.counted":           { en: "Counted",                    el: "Μετρημένο",                  ar: "المعدود" },
  "endShift.discrepancy":       { en: "Discrepancy",                el: "Απόκλιση",                   ar: "الفرق" },
  "endShift.saveAndClose":      { en: "SAVE EVERYTHING & CLOSE",    el: "ΑΠΟΘΗΚΕΥΣΗ & ΚΛΕΙΣΙΜΟ ΟΛΩΝ", ar: "حفظ كل شيء وإغلاق" },
  "endShift.saveSubtitle":      { en: "Archive & Close Shift",      el: "Αρχειοθέτηση & Κλείσιμο Βάρδιας", ar: "أرشفة وإغلاق الوردية" },
  "endShift.archiving":         { en: "ARCHIVING…",                 el: "ΑΡΧΕΙΟΘΕΤΗΣΗ…",              ar: "جارٍ الأرشفة…" },
  "endShift.blocked":           { en: "BLOCKED · Resolve Pending Drafts", el: "ΕΜΠΟΔΙΟ · Επιλύστε εκκρεμότητες", ar: "محظور · حل المعلقات" },
  "endShift.blockedSubtitle":   { en: "Resolve pending drafts before closing", el: "Επιλύστε εκκρεμότητες πριν το κλείσιμο", ar: "حل المسودات المعلقة قبل الإغلاق" },

  // ── Banner (trilingual lines stack as primary→secondary→tertiary)
  "banner.title.primary":       { en: "⚠️ ATTENTION: Pending entries found! You must click \"+ ADD TO SHIFT\" before closing.",
                                  el: "⚠️ ΠΡΟΣΟΧΗ: Υπάρχουν εκκρεμείς εγγραφές! Πρέπει να κάνετε \"+ ADD TO SHIFT\" στα Τιμολόγια ή στο Cashflow για να επιτραπεί το κλείσιμο της βάρδιας.",
                                  ar: "⚠️ تنبيه: تم العثور على إدخالات معلقة! يجب النقر فوق \"+ إضافة إلى الوردية\" في الفواتير أو الحركة النقدية قبل إغلاق الوردية." },
  "banner.clientDraft.voucher": { en: "• Cashflow voucher staged on Invoices tab — review and tap + ADD TO SHIFT.",
                                  el: "• Πληρωμή ταμείου σε αναμονή στα Τιμολόγια — ελέγξτε και πατήστε + ADD TO SHIFT.",
                                  ar: "• قسيمة نقدية في انتظار في علامة تبويب الفواتير — راجع واضغط على + إضافة إلى الوردية." },
  "banner.clientDraft.ocr":     { en: "• Invoice OCR preview open — confirm or discard before closing.",
                                  el: "• Σάρωση τιμολογίου σε προεπισκόπηση — επιβεβαιώστε ή απορρίψτε πριν το κλείσιμο.",
                                  ar: "• معاينة OCR للفاتورة مفتوحة — أكد أو تجاهل قبل الإغلاق." },
  "banner.goToInvoices":        { en: "Go to Invoices",              el: "Πήγαινε στα Τιμολόγια",     ar: "اذهب إلى الفواتير" },

  // ── Sign-In (SaaS)
  "saasLogin.title":            { en: "Sign in",                    el: "Σύνδεση",                    ar: "تسجيل الدخول" },
  "saasLogin.subtitle":         { en: "Welcome back to KioskForce ERP", el: "Καλώς ήρθατε πίσω στο KioskForce ERP", ar: "مرحباً بعودتك إلى KioskForce ERP" },
  "saasLogin.email":            { en: "Email",                      el: "Email",                      ar: "البريد الإلكتروني" },
  "saasLogin.password":         { en: "Password",                   el: "Κωδικός",                    ar: "كلمة المرور" },
  "saasLogin.signIn":           { en: "Sign In",                    el: "Σύνδεση",                    ar: "تسجيل الدخول" },
  "saasLogin.noAccount":        { en: "No account yet?",             el: "Δεν έχετε λογαριασμό;",      ar: "ليس لديك حساب بعد؟" },
  "saasLogin.createOne":        { en: "Create one — 2-month free trial", el: "Δημιουργήστε έναν — δωρεάν 2μηνη δοκιμή", ar: "أنشئ واحدًا — تجربة مجانية لمدة شهرين" },

  // ── Register
  "register.title":             { en: "Start your free trial",      el: "Ξεκινήστε δωρεάν δοκιμή",    ar: "ابدأ تجربتك المجانية" },
  "register.subtitle":          { en: "2 months free · no card required", el: "2 μήνες δωρεάν · χωρίς κάρτα",    ar: "شهران مجانًا · بدون بطاقة" },
  "register.companyName":       { en: "Company Name",               el: "Όνομα Εταιρείας",            ar: "اسم الشركة" },
  "register.email":             { en: "Work Email",                 el: "Email Εργασίας",             ar: "البريد الإلكتروني للعمل" },
  "register.password":          { en: "Password",                   el: "Κωδικός",                    ar: "كلمة المرور" },
  "register.create":            { en: "Create my account",          el: "Δημιουργία λογαριασμού",     ar: "إنشاء حسابي" },
  "register.alreadyHave":       { en: "Already have an account?",    el: "Έχετε ήδη λογαριασμό;",      ar: "هل لديك حساب بالفعل؟" },
  "register.signIn":            { en: "Sign in",                    el: "Συνδεθείτε",                 ar: "تسجيل الدخول" },
};

// Resolver helper — flat lookup with EN fallback.
function resolve(key, code) {
  const entry = DICT[key];
  if (!entry) return key;
  return entry[code] ?? entry.en ?? key;
}

// ─────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────
const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (k) => k,
  dir: "ltr",
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED_LANGS.some((l) => l.code === stored)) return stored;
    } catch { /* no-op */ }
    return DEFAULT_LANG;
  });

  // Reflect language + direction on the root <html> element so RTL
  // cascades through every dialog/portal child without bespoke logic.
  useEffect(() => {
    const dir = isRTL(lang) ? "rtl" : "ltr";
    const root = document.documentElement;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", dir);
    // Toggle a marker class so Tailwind-based styles can scope on it.
    if (isRTL(lang)) root.classList.add("rtl");
    else root.classList.remove("rtl");
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* no-op */ }
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang: setLangState,
      t: (key) => resolve(key, lang),
      dir: isRTL(lang) ? "rtl" : "ltr",
    }),
    [lang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
export const useT = () => useContext(I18nContext).t;
export const useLang = () => useContext(I18nContext).lang;
